/** @format */

import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FadeLoader from "react-spinners/FadeLoader";
import Footer from "../components/Footer";
import Headers from "../components/Headers";
import { useCategories } from "../utils/Categories";
import { devise } from "../utils/utils";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const PAYMENT_OPTIONS = [
  {
    method: "stripe",
    imageSrc: "/images/payment/strippe.png",
    label: "Stripe",
    color: "blue-500",
    bgColor: "bg-blue-500",
    description: "Pay securely with your credit card via Stripe.",
  },
  {
    method: "payPal",
    imageSrc: "/images/payment/Paypal.png",
    label: "PayPal",
    color: "blue-800",
    bgColor: "bg-blue-800",
    description: "Fast checkout with your PayPal account.",
  },
  {
    method: "mastercard",
    imageSrc: "/images/payment/MasterCard.png",
    label: "MasterCard",
    color: "orange-700",
    bgColor: "bg-orange-700",
    description: "Pay with your MasterCard directly.",
  },
  {
    method: "visa",
    imageSrc: "/images/payment/visa.png",
    label: "Visa",
    color: "indigo-600",
    bgColor: "bg-indigo-600",
    description: "Pay with your Visa card securely.",
  },
  {
    method: "mobileMoney",
    imageSrc: "/images/payment/mobileMoney.png",
    label: "Mobile Money",
    color: "green-600",
    bgColor: "bg-green-600",
    description: "Pay with Orange Money, Wave, or MTN.",
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const LoadingOverlay = () => (
  <div
    className='w-screen h-screen flex justify-center items-center
      fixed inset-0 bg-gray-900/60 z-[99999] backdrop-blur-sm'
    aria-busy='true'
    aria-label='Loading'
  >
    <FadeLoader color='#f97316' />
  </div>
);

const ErrorScreen = ({ message }) => (
  <div
    className='min-h-[40vh] flex flex-col justify-center items-center
      gap-3 text-slate-600'
  >
    <svg
      className='w-12 h-12 text-red-400'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={1.5}
        d='M12 9v3m0 3h.01M10.29 3.86L1.82
           18a2 2 0 001.71 3h16.94a2 2 0
           001.71-3L13.71 3.86a2 2 0 00-3.42 0z'
      />
    </svg>
    <p className='text-lg font-medium text-red-500'>{message}</p>
  </div>
);

// ---------------------------------------------------------------------------

const PaymentOptionCard = ({
  method,
  imageSrc,
  label,
  currentMethod,
  onClick,
}) => {
  const isActive = currentMethod === method;

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick(method);
      }
    },
    [method, onClick],
  );

  return (
    <button
      type='button'
      role='radio'
      aria-checked={isActive}
      tabIndex={0}
      onClick={() => onClick(method)}
      onKeyDown={handleKey}
      className={`
        relative flex-1 min-w-[18%] flex flex-col items-center gap-2
        py-4 px-3 border-b-2 transition-all duration-200 cursor-pointer
        focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400
        ${
          isActive
            ? "border-orange-500 bg-white shadow-md"
            : "border-transparent bg-slate-100 hover:bg-slate-200"
        }
      `}
    >
      {isActive && (
        <span
          className='absolute top-2 right-2 w-2 h-2 rounded-full
            bg-orange-500 animate-pulse'
        />
      )}
      <img
        src={imageSrc}
        alt={label}
        className='w-8 h-8 object-contain'
        loading='lazy'
      />
      <span
        className={`text-xs font-semibold tracking-wide
          ${isActive ? "text-orange-500" : "text-slate-500"}`}
      >
        {label}
      </span>
    </button>
  );
};

// ---------------------------------------------------------------------------

const PaymentPanel = ({ option, t }) => {
  if (!option) return null;

  return (
    <div className='w-full px-6 py-8 bg-white shadow-md border-t-0 rounded-b-lg'>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center gap-3'>
          <img
            src={option.imageSrc}
            alt={option.label}
            className='w-10 h-10 object-contain'
          />
          <div>
            <h3 className='font-bold text-slate-700 text-base'>
              {option.label}
            </h3>
            <p className='text-sm text-slate-400'>{option.description}</p>
          </div>
        </div>

        <button
          type='button'
          className={`
            w-full sm:w-auto px-8 py-3 rounded-lg font-semibold text-white
            text-sm tracking-wide transition-all duration-200
            hover:opacity-90 hover:shadow-lg active:scale-[0.98]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
            focus-visible:ring-orange-400
            ${option.bgColor}
          `}
        >
          {t("Pay with")} {option.label}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const OrderSummary = ({ items, price, orderId, t }) => (
  <div
    className='bg-white rounded-xl shadow-md overflow-hidden
      border border-slate-100'
  >
    {/* Header */}
    <div className='px-5 py-4 border-b border-slate-100 bg-slate-50'>
      <h2 className='font-bold text-slate-700 text-base tracking-wide'>
        {t("Order Summary")}
      </h2>
    </div>

    {/* Body */}
    <div className='px-5 py-4 flex flex-col gap-3'>
      <div className='flex justify-between items-center text-sm text-slate-500'>
        <span>
          {items} {t("items and shipping fees included")}
        </span>
        <span className='font-medium text-slate-700'>
          {Number(price).toFixed(2)} {devise}
        </span>
      </div>

      <div className='h-px bg-slate-100' />

      <div className='flex justify-between items-center'>
        <span className='font-semibold text-slate-700 text-sm'>
          {t("Total Amount")}
        </span>
        <span className='text-lg font-bold text-orange-500'>
          {Number(price).toFixed(2)} {devise}
        </span>
      </div>
    </div>

    {/* CTA */}
    <div className='px-5 pb-5 pt-1'>
      <Link
        to={`/dashboard/order/details/${orderId}`}
        className='
          flex items-center justify-center gap-2 w-full py-3
          rounded-lg bg-orange-500 hover:bg-orange-600
          text-white text-sm font-semibold tracking-wide
          transition-all duration-200 hover:shadow-lg
          hover:shadow-orange-500/30 active:scale-[0.98]
          focus:outline-none focus-visible:ring-2
          focus-visible:ring-orange-400 focus-visible:ring-offset-2
        '
      >
        <svg
          className='w-4 h-4'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
          />
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M2.458 12C3.732 7.943 7.523 5
               12 5c4.478 0 8.268 2.943
               9.542 7-1.274 4.057-5.064
               7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
          />
        </svg>
        {t("Voir details")}
      </Link>
    </div>
  </div>
);

// ---------------------------------------------------------------------------

const InfoBanner = ({ message }) => (
  <div
    className='flex items-start gap-3 p-4 rounded-xl bg-amber-50
      border border-amber-200 text-amber-800'
  >
    <svg
      className='w-5 h-5 mt-0.5 shrink-0 text-amber-500'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M13 16h-1v-4h-1m1-4h.01M21
           12a9 9 0 11-18 0 9 9 0 0118 0z'
      />
    </svg>
    <p className='text-sm leading-relaxed'>{message}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const Payment = () => {
  const [paymentMethod, setPaymentMethod] = React.useState(
    PAYMENT_OPTIONS[0].method,
  );

  const { categories, loading, errorMessage, clientLocation } = useCategories();
  const {
    state: { price, items, orderId },
  } = useLocation();
  const { customerInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Redirect unauthenticated users
  useEffect(() => {
    if (!customerInfo) {
      navigate("/", { replace: true });
    }
  }, [customerInfo, navigate]);

  const activeOption = useMemo(
    () => PAYMENT_OPTIONS.find((o) => o.method === paymentMethod),
    [paymentMethod],
  );

  if (loading) return <LoadingOverlay />;
  if (errorMessage)
    return <ErrorScreen message={`${t("Error")}: ${errorMessage}`} />;
  if (!customerInfo) return null;

  return (
    <div className='min-h-screen flex flex-col bg-[#f4f4f3]'>
      <Headers categories={categories} clientLocation={clientLocation} />

      <section className='flex-1'>
        <div
          className='w-[85%] lg:w-[90%] md:w-[95%] sm:w-full
            mx-auto py-12 mt-4'
        >
          {/* Page title */}
          <h1 className='text-2xl font-bold text-slate-700 mb-8 tracking-tight'>
            {t("Checkout")}
            <span className='ml-2 text-sm font-normal text-slate-400'>
              — {t("Choose your payment method")}
            </span>
          </h1>

          <div className='flex flex-wrap md:flex-col-reverse gap-6'>
            {/* ---- Left: Payment methods ---- */}
            <div className='flex-1 min-w-0'>
              {/* Method tabs */}
              <div
                className='flex flex-wrap rounded-t-xl overflow-hidden
                  border border-slate-200 bg-slate-100'
                role='radiogroup'
                aria-label='Payment methods'
              >
                {PAYMENT_OPTIONS.map((option) => (
                  <PaymentOptionCard
                    key={option.method}
                    method={option.method}
                    imageSrc={option.imageSrc}
                    label={option.label}
                    currentMethod={paymentMethod}
                    onClick={setPaymentMethod}
                  />
                ))}
              </div>

              {/* Active panel */}
              <div className='rounded-b-xl border border-t-0 border-slate-200 overflow-hidden'>
                <PaymentPanel option={activeOption} t={t} />
              </div>
            </div>

            {/* ---- Right: Order summary ---- */}
            <div className='w-[36%] md:w-full flex flex-col gap-4'>
              <OrderSummary
                items={items}
                price={price}
                orderId={orderId}
                t={t}
              />
              <InfoBanner
                message={t(
                  "Le paiement direct est temporairement indisponible. Accedez aux details de la commande pour contacter le vendeur directement.",
                )}
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Payment;
